import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import "./index.css";

import Button from "../../../Button/Button";
import * as ReactBootstrap from "react-bootstrap";
import * as api from "../../../api";

import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Modal from "../../Modal";
import {
  clearReferralInformation,
  comingFromReferralPage,
  saveReferralAccountBalance,
} from "../../../ducks/redux/actions/FinancialReferral";
import { userChosenAccountNumber } from "../../../ducks/redux/actions/userChoices";
import { BalanceType } from "../../../models/Accounts/BalanceType";
import LoadingSpinner from "../../../storybook/LoadingSpinner";

interface ReviewrReferralProps extends RouteComponentProps<any> {}

type NospResponseOption = {
  id: number;
  text: string;
};

type NospDependsOn = {
  questionId: number;
  responseId: number;
};

type NospQuestion = {
  questionId: number;
  label: string;
  sequence: number;
  type: "Radio" | "Date";
  allowedChannels: string[];
  responseOptions?: NospResponseOption[];
  dependsOn?: NospDependsOn[];
};

type NospReferenceData = {
  namespace: {
    name: string;
    keyValues: Record<string, NospQuestion>;
  };
};

type NospEntry =
  | { questionId: number; type: "Radio"; responseId: number }
  | { questionId: number; type: "Date"; value: string };

type ReviewRow = {
  key: string;
  labelKey: string;
  value: string;
  isEditable: boolean;
  onEdit?: () => void;
};

const FinancialReferralReview: React.FC<ReviewrReferralProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [borough, setBorough] = useState("");
  const [balance, setBalance] = useState("");
  const [contactType, setContactType] = useState("");
  const [continueButtonDisable, setContinueButtonDisable] = useState(false);
  const [isExitOpen, setIsExitOpen] = useState(false);

  const [isBoroughLoading, setIsBoroughLoading] = useState(true);
  const [isBalanceLoading, setIsBalanceLoading] = useState(true);

  const financialData = useSelector(
    (state: any) => state.financialReferralReducer.financialReferralData,
  );
  const referralDetail = useSelector(
    (state: any) => state.financialReferralReducer.referralDetails,
  );
  const accBalance = useSelector(
    (state: any) => state.financialReferralReducer.referralAccountBalance,
  );
  const nospReferenceData = useSelector(
    (state: any) => state.financialReferralReducer?.nospReferenceData,
  );

  const assetId = useSelector(
    (state: any) => state?.userChoicesReducer?.defaultAssetId,
  );
  const payRef = useSelector((state: any) => state.userChoicesReducer.accountnumber);

  const shareContactType = useSelector(
    (state: any) => state?.addresses?.customerPhoneNumber || [],
  );

  const customerData = useSelector((state: any) => state?.userChoicesReducer?.customersData);

  const userChosenPropertyAddress = useSelector(
    (state: any) => state?.userChoicesReducer?.userChosenPropertyAddress,
  );

  const contactNo = useSelector(
    (state: any) => state.userChoicesReducer.userChosenContactPhone || null,
  );

  const contactName = useSelector(
    (state: any) => state.userChoicesReducer.userChosenContactName || null,
  );

  const contactRelation = useSelector(
    (state: any) => state.userChoicesReducer.userChosenRelationDetails || null,
  );

  const nospDetails = useSelector(
    (state: any) => state.financialReferralReducer?.nospDetailsPayload?.nospDetails,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchBalance = async () => {
      if (!payRef) {
        setBalance("");
        setIsBalanceLoading(false);
        return;
      }

      try {
        setIsBalanceLoading(true);
        const accountBalance = await api.getAccount(payRef);
        accountBalance["balance"].forEach((b: any) => {
          if (b["balance-type"] === BalanceType.Livebalanced) {
            const amount = b["monetary-amount"]["value"];
            setBalance(amount < 0 ? `-£${Math.abs(amount)}` : `£${amount}`);           
          }
        });
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.log("Error:", e);
      } finally {
        setIsBalanceLoading(false);
      }
    };

    const mapContactType = () => {
      (shareContactType || []).forEach((x: any) => {
        if (x?.number === contactNo) {
          setContactType(x?.telecomsAddressType || "");
        }
      });
    };

    fetchBalance();
    mapContactType();
  }, [payRef, shareContactType, contactNo]);

  useEffect(() => {
    const fetchBorough = async () => {
      try {
        setIsLoading(true);
        setIsBoroughLoading(true);
        const associatesdata = await api.getAssetsByid(assetId);
        setBorough(associatesdata?.borough || "");
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.log("Error:", e);
      } finally {
        setIsBoroughLoading(false);
        setIsLoading(false);
      }
    };

    if (assetId) {
      fetchBorough();
    } else {
      setBorough("");
      setIsBoroughLoading(false);
      setIsLoading(false);
    }
  }, [assetId]);

  const isPageLoading = isLoading || isBoroughLoading || isBalanceLoading;

  const nospSchema: NospReferenceData | null = useMemo(() => {
    if (Array.isArray(nospReferenceData)) {
      return nospReferenceData[0] || null;
    }
    return nospReferenceData || null;
  }, [nospReferenceData]);

  const title = customerData?.person?.name?.title ?? "";
  const fullName = `${title} ${customerData?.person?.name?.givenName ?? ""} ${customerData?.person?.name?.surname ?? ""}`.trim();

  const referralReasonText = Array.isArray(financialData?.ReferralReason)
    ? financialData.ReferralReason.join(", ")
    : "";

  const isDebtSelected = Array.isArray(financialData?.ReferralReason)
    ? financialData.ReferralReason.includes("Debt")
    : false;

  const contactNoText =
    typeof contactNo === "string" ? contactNo.split("@")[0] : (contactNo || "");

  const contactInfo =
    contactName && contactRelation
      ? `${contactNoText}, ${contactName}, ${contactRelation}`
      : contactNoText;

  const formatDebtValues = (value: any): string => {
    const arr: any[] = Array.isArray(value) ? value : [];
    const texts = arr
      .map((x) => {
        if (!x) return "";
        if (typeof x === "string") return x;
        if (typeof x?.text === "string") return x.text;
        return "";
      })
      .filter(Boolean);

    return texts.length > 0 ? texts.join(", ") : t("Financial_NotApplicable");
  };

  const findQuestionIdByLabel = (label: string): number | "" => {
    const kv = nospSchema?.namespace?.keyValues;
    if (!kv) return "";
    const match = Object.values(kv).find((q: NospQuestion) => q?.label === label);
    return match?.questionId || "";
  };

  const getNospRadioResponseId = (questionId: number): number | "" => {
    const details: NospEntry[] = Array.isArray(financialData?.NospDetails)
      ? (financialData.NospDetails as NospEntry[])
      : [];

    const entry = details.find(
      (x: any) => x?.type === "Radio" && x?.questionId === questionId,
    ) as any;

    return entry?.responseId || "";
  };

  const getNospRadioAnswerText = (questionId: number): string => {
    const responseId = getNospRadioResponseId(questionId);
    if (!responseId) return "";

    const q = nospSchema?.namespace?.keyValues?.[String(questionId)];
    const options: NospResponseOption[] = Array.isArray(q?.responseOptions) ? q?.responseOptions ?? [] : [];
    const opt = options.find((o) => o?.id === responseId);

    if (opt?.text) return opt.text;

    if (responseId === 1) return "Yes";
    if (responseId === 2) return "No";
    if (responseId === 3) return "Don't know";
    return "";
  };

  const nospRootQuestionId = useMemo(() => {
    const kv = nospSchema?.namespace?.keyValues;
    if (!kv) return "";
    return kv["1"] ? 1 : "";
  }, [nospSchema]);

  const nospServedResponseId = useMemo(() => {
    if (!nospRootQuestionId) return "";
    return getNospRadioResponseId(nospRootQuestionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nospRootQuestionId, financialData?.NospDetails]);

  const nospServedAnswer = useMemo(() => {
    if (!nospRootQuestionId) return "";
    return getNospRadioAnswerText(nospRootQuestionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nospRootQuestionId, nospSchema, financialData?.NospDetails]);

  const showNospFollowUps = nospServedResponseId === 1 || nospServedAnswer === "Yes";

  const nospQuestionIdCourtDateSet = useMemo(
    () => findQuestionIdByLabel(t("Financial_Review_CourtDateSet")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const nospQuestionIdCourtOrderIssued = useMemo(
    () => findQuestionIdByLabel(t("Financial_Review_CourtOrderIssued")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const nospQuestionIdEvictionDate = useMemo(
    () => findQuestionIdByLabel(t("Financial_Review_EvictionDate")),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const nospQuestionIdCourtDateSetFallback = useMemo(
    () => findQuestionIdByLabel("Has a court date been set?"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const nospQuestionIdCourtOrderIssuedFallback = useMemo(
    () => findQuestionIdByLabel("Has a court order been issued?"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const nospQuestionIdEvictionDateFallback = useMemo(
    () => findQuestionIdByLabel("Is there an eviction date?"),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nospSchema],
  );

  const courtDateSetAnswer = useMemo(() => {
    const id = nospQuestionIdCourtDateSet || nospQuestionIdCourtDateSetFallback;
    if (!id) return "";
    return getNospRadioAnswerText(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nospQuestionIdCourtDateSet, nospQuestionIdCourtDateSetFallback, nospSchema, financialData?.NospDetails]);

  const courtOrderIssuedAnswer = useMemo(() => {
    const id = nospQuestionIdCourtOrderIssued || nospQuestionIdCourtOrderIssuedFallback;
    if (!id) return "";
    return getNospRadioAnswerText(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nospQuestionIdCourtOrderIssued, nospQuestionIdCourtOrderIssuedFallback, nospSchema, financialData?.NospDetails]);

  const evictionDateAnswer = useMemo(() => {
    const id = nospQuestionIdEvictionDate || nospQuestionIdEvictionDateFallback;
    if (!id) return "";
    return getNospRadioAnswerText(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nospQuestionIdEvictionDate, nospQuestionIdEvictionDateFallback, nospSchema, financialData?.NospDetails]);

  const rows: ReviewRow[] = useMemo(() => {
    const base: ReviewRow[] = [
      { key: "fullName", labelKey: "Financial_Review_FullName", value: fullName, isEditable: false },
      {
        key: "referralFor",
        labelKey: "Financial_Review_ReferralFor",
        value: financialData?.referralFor || "",
        isEditable: true,
        onEdit: () => props.history.push("/CustomerConsent"),
      },
      {
        key: "tenantConsent",
        labelKey: "Financial_Review_TenantConsent",
        value: financialData?.FinancialInclusionTeam || "",
        isEditable: true,
        onEdit: () => props.history.push("/CustomerConsent"),
      },
      {
        key: "propertyAddress",
        labelKey: "Financial_Review_PropertyAddress",
        value: userChosenPropertyAddress || "",
        isEditable: false,
      },
      { key: "borough", labelKey: "Financial_Review_Borough", value: borough || "", isEditable: false },
      {
        key: "reasonForReferral",
        labelKey: "Financial_Review_ReasonForReferral",
        value: referralReasonText || "",
        isEditable: true,
        onEdit: () => props.history.push("/referralreason"),
      },
    ];

    const debtPart: ReviewRow[] = [];
    if (isDebtSelected) {
      debtPart.push(
        {
          key: "priorityDebts",
          labelKey: "Financial_Review_PriorityDebts",
          value: formatDebtValues(financialData?.ReferralPriorityDebtTypes),
          isEditable: true,
          onEdit: () => props.history.push("/referraldebtquestion"),
        },
        {
          key: "nonPriorityDebts",
          labelKey: "Financial_Review_NonPriorityDebts",
          value: formatDebtValues(financialData?.ReferralNonPriorityDebtTypes),
          isEditable: true,
          onEdit: () => props.history.push("/referraldebtquestion"),
        },
      );
    }

    const nospPart: ReviewRow[] = [
      {
        key: "nospServed",
        labelKey: "Financial_Review_NospServed",
        value: nospServedAnswer || "",
        isEditable: true,
        onEdit: () => props.history.push("/nospform"),
      },
    ];

    if (showNospFollowUps) {
      nospPart.push(
        {
          key: "courtDateSet",
          labelKey: "Financial_Review_CourtDateSet",
          value: courtDateSetAnswer || "",
          isEditable: true,
          onEdit: () => props.history.push("/nospform"),
        },
        {
          key: "courtOrderIssued",
          labelKey: "Financial_Review_CourtOrderIssued",
          value: courtOrderIssuedAnswer || "",
          isEditable: true,
          onEdit: () => props.history.push("/nospform"),
        },
        {
          key: "evictionDate",
          labelKey: "Financial_Review_EvictionDate",
          value: evictionDateAnswer || "",
          isEditable: true,
          onEdit: () => props.history.push("/nospform"),
        },
      );
    }

    const tail: ReviewRow[] = [
      {
        key: "additionalDetails",
        labelKey: "Financial_Review_AdditionalReferralDetails",
        value: referralDetail || "",
        isEditable: true,
        onEdit: () => props.history.push("/referraladditionaldetails"),
      },
      {
        key: "preferredContact",
        labelKey: "Financial_Review_PreferredContactMethod",
        value: contactInfo || "",
        isEditable: true,
        onEdit: () => {
          dispatch(comingFromReferralPage(true));
          props.history.push("/ContactDetails");
        },
      },
      {
        key: "accountBalance",
        labelKey: "Financial_Review_AccountBalance",
        value: balance || (accBalance || ""),
        isEditable: false,
      },
      {
        key: "paymentReference",
        labelKey: "Financial_Review_PaymentReference",
        value: payRef || "",
        isEditable: false,
      },
    ];

    return [...base, ...debtPart, ...nospPart, ...tail];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fullName,
    financialData,
    userChosenPropertyAddress,
    borough,
    referralReasonText,
    isDebtSelected,
    nospServedAnswer,
    showNospFollowUps,
    courtDateSetAnswer,
    courtOrderIssuedAnswer,
    evictionDateAnswer,
    referralDetail,
    contactInfo,
    balance,
    accBalance,
    payRef,
    dispatch,
    props.history,
    t,
  ]);

  const bodyData = useMemo(() => {
    const phoneNumberForPayload =
      typeof contactNo === "string" && contactNo.includes("@")
        ? contactNo.split("@")[0]
        : (contactNo || "");
    const base = {
      additionalDetails: referralDetail,
      referralReason: financialData?.ReferralReason,
      referralFor: financialData?.referralFor,
      assetIdentifier: assetId,
      hasTenantConsent: financialData?.FinancialInclusionTeam === "Yes",
      serviceRequestType: "financialinclusionreferral",
      preferredContact: {
        personName: contactName || "",
        relationship: contactRelation || "",
        phoneNumber: phoneNumberForPayload,
        phoneNumberType: contactType || "",
      },
    };

    const coerceDebtValue = (value: any): { key: string; text: string } | null => {
      if (!value) {
        return null;
      }
      if (typeof value === "string") {
        return { key: value, text: value };
      }
      const key = typeof value?.key === "string" ? value.key : "";
      const text = typeof value?.text === "string" ? value.text : "";
      if (!key && !text) {
        return null;
      }
      return { key: key || text, text: text || key };
    };

    const priorityDebtValues = (Array.isArray(financialData?.ReferralPriorityDebtTypes)
      ? (financialData.ReferralPriorityDebtTypes as any[])
      : []
    )
      .map(coerceDebtValue)
      .filter(Boolean) as { key: string; text: string }[];

    const nonPriorityDebtValues = (Array.isArray(financialData?.ReferralNonPriorityDebtTypes)
      ? (financialData.ReferralNonPriorityDebtTypes as any[])
      : []
    )
      .map(coerceDebtValue)
      .filter(Boolean) as { key: string; text: string }[];

    const referralReasonDetails = isDebtSelected
      ? [
          {
            relatedReferralReason: "Debt",
            metadata: [
              { key: "PRIORITY_DEBTS", values: priorityDebtValues },
              { key: "NON_PRIORITY_DEBTS", values: nonPriorityDebtValues },
            ],
          },
        ]
      : [];

    return {
      ...base,
      referralReasonDetails,
      nospDetails,
    };
  }, [
    referralDetail,
    financialData,
    assetId,
    contactName,
    contactRelation,
    contactNo,
    contactType,
    nospDetails,
    isDebtSelected,
  ]);

  const exitButtonHandler = () => {
    dispatch(clearReferralInformation({}));
    dispatch(saveReferralAccountBalance(""));
    dispatch(userChosenAccountNumber(""));
    props.history.push("/Customer360View");
  };

  const onBackHandler = () => {
    dispatch(comingFromReferralPage(true));
    props.history.push("/ContactDetails");
  };

  const onContinueHandler = async () => {
    setContinueButtonDisable(true);
    try {
      setIsLoading(true);
      await api.submitFinancialInclusion(customerData["customer-identifier"], payRef, bodyData);
      props.history.push("/referralConfirmation");
    } catch (e: any) {
      setError("Financial_FI013c");
      setContinueButtonDisable(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="container px-2 sm:mx-1 ctn-fht" id="referralreview-container" data-testid="referralreview-container">
      {isPageLoading ? (
        <div className="div-loader marign-left-smallscreen-0">
          <div className="SpinnerWrapper">
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <>
          <div id="backmodal-div1" className="mx-auto mb-0 md:mb-14 lg:mb-10 px-0 xl:px-0">
            <p id="backmodal-p" className="text-teal text-xl mt-4 mb-8">
              <button data-testid="custBackBtn" className="back-btn paddingleft0" onClick={onBackHandler}>
                <i className="fas fa-angle-left"></i>
                <span>{t("back")}</span>
              </button>
            </p>
          </div>

          <div>
            <div id="confirmation-main-div" className="global-txt">
              <h1 data-testid="case-title" className="text-purple-100 text-2xl mb-2">
                {t("Financial_FI013a")}
              </h1>
              <p data-testid="case-title-t1" className="mb-4 color_black"></p>
              <p data-testid="case-title-t2" className="mb-4 color_black">
                {t("Financial_FI014a")}
              </p>
            </div>

            <div className="repair-text" id="confirmation-div-table">
              <ReactBootstrap.Table striped className="container-new">
                <TableBody role="presentation" id="confirmation-table" className="w-full border border-grey">
                  <th className="hidden">{t("table_heading")}</th>

                  {rows.map((row: ReviewRow) => (
                    <TableRow id={"confirmation-tr1"} className="flex items-center bg-white" key={row.key}>
                      <TableCell id={"confirmation-tr1-td4"} className="w-5/12 p-3 firstcol">
                        <div className="text-base padding-new color_black global-txt">{t(row.labelKey)}</div>
                      </TableCell>

                      <TableCell id={"confirmation-tr1-td4"} className="w-5/12 p-3 firstcol">
                        <div className="text-base padding-new color_black global-txt">{row.value}</div>
                      </TableCell>

                      <TableCell id={"confirmation-tr1-td4"} className="w-5/12 p-3 firstcol">
                        <div className="text-base padding-new color_black global-txt">
                          {row.isEditable ? (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <a
                              data-testid="editBtn"
                              className="edit-btn"
                              style={{ cursor: "pointer" }}
                              onClick={row.onEdit}
                            >
                              <i
                                id={"confirmation-tr1-icon"}
                                className="fas fa-edit text-teal text-lg"
                              ></i>
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ReactBootstrap.Table>
            </div>
          </div>

          <div data-testid="alert-warning" className="error px-2 py-2" role="alert" placeholder="alert-message">
            {error && (
              <div className="w-full px-2 py-2 bg-red-20 flex items-center">
                <div className="px-4 pr-8">
                  <i className="fas fa-exclamation-triangle text-brick-70 w-6 h-6"></i>
                </div>
                <div className="flex items-center">
                  <p data-testid="locatioAlert_0" className="w-full text-left my-2 font-AvantGardeGothic-Md">
                    {t(error)}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center pb-4 md:pb-8 px-2" id="sms-btn" style={{ marginTop: '16px' }}>
            <button
              type="button"
              className="exit-btn link-like-btn"
              data-testid="Review-CancelAndExit"
              style={{ cursor: "pointer", background: "none", border: "none", padding: 0, color: "#00f2ff", textDecoration: "underline" }}
              onClick={() => setIsExitOpen(true)}
              aria-label={t("Exit")}
            >
              {t("Exit")}
            </button>
            <Button
              disabled={continueButtonDisable}
              id="ReviewContinue"
              type="button"
              data-testid="Review-Continue"
              name="Continue"
              onClick={onContinueHandler}
              value="Continue"
              className="button-class"
            >
              {t("Continue")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
            </Button>
          </div>

          <Modal open={isExitOpen} cssClasses="modalPopUp">
            <div id="exitmodal-div1" className="modalPopUpContainer container my-1">
              <div
                id="exitmodal-div2"
                className="py-lg-0 py-sm-0 h5 my-5 question-font-size text-center global-txt"
              >
                {t("SSR030ai")}
                <br></br>
                <br></br>
                {t("SSR030aii")}
              </div>

              <div
                id="exitmodal-div3"
                className="modalButtonContainer button-positions flex flex-wrap text-center pad-bot pt-lg-0"
              >
                <Button id="exitmodal-btn2" className="button-class" onClick={() => setIsExitOpen(false)}>
                  {t("Financial_ReturnToMyReferral")}
                  <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
                </Button>

                <Button id="exitmodal-btn3" onClick={exitButtonHandler} className="button-class">
                  {t("leave_button")}
                  <i className="fas fa-angle-right relativetext-white pointer-events-none arrow ms-2"></i>
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </main>
  );
};

export default FinancialReferralReview;